import { ModalDefinitionKind, MessageModalDefinition } from "@/store/modals/types";
import type { ModalsStore } from "@/store/modals/ModalsStore";
import type { AppStoreNavigationStore } from "@/store/navigation/AppStoreNavigationStore";
import { Uuid } from "@/domains/global/identifiers";

export enum RecordingFailureReason {
  FailedToUpload = "failed-to-upload",
  FailedToProcess = "failed-to-process",
  NoContentDetected = "no-content-detected",
}

function getMessageForReason(reason: RecordingFailureReason): string {
  switch (reason) {
    case RecordingFailureReason.FailedToUpload:
      return "We were unable to upload your voice recording. You'll need to try uploading again.";
    case RecordingFailureReason.FailedToProcess:
      return "We were unable to process your voice recording. You'll need to try processing again.";
    case RecordingFailureReason.NoContentDetected:
      return "No audio content was detected in your recording. The recording may have been too short.";
  }
}

export function enqueueRecordingFailureModal({
  modals,
  navigation,
  reason,
  associatedNoteId,
}: {
  modals: ModalsStore;
  navigation: Pick<AppStoreNavigationStore, "goToNote" | "isCurrentlyOnNotePage" | "currentItemId">;
  reason: RecordingFailureReason;
  associatedNoteId?: Uuid | null;
}) {
  if (
    associatedNoteId &&
    navigation.isCurrentlyOnNotePage &&
    navigation.currentItemId === associatedNoteId
  ) {
    return;
  }

  const closeCurrentModal = () => {
    const current = modals.current;

    if (current) {
      modals.removeModal(current);
    }
  };

  const primaryLabel = associatedNoteId ? "View note" : "Dismiss";
  const message = getMessageForReason(reason);

  const modal: MessageModalDefinition = {
    kind: ModalDefinitionKind.Message,
    modal: {
      title: "Something went wrong",
      message,
      buttonLabel: primaryLabel,
      handleButtonClick: () => {
        closeCurrentModal();
        if (associatedNoteId) {
          navigation.goToNote({ noteId: associatedNoteId, openInSameTab: true });
        }
      },
      handleClose: closeCurrentModal,
      secondaryButton: associatedNoteId
        ? {
            label: "Dismiss",
            onClick: closeCurrentModal,
          }
        : undefined,
    },
  };

  modals.addModal(modal, { priority: true });
}
